import {
	RegulatoryDocument,
	RegulatoryDocumentDisassemblyStatus,
	RegulatoryDocumentParagraph,
	WorkflowStatus,
} from 'types';
import {TFunction} from 'react-i18next';
import i18n from 'i18n/i18n';
import {formatDateTime} from 'i18n/localeDateFormat';
import Excel from 'exceljs';
import {funcDecodeHex24ToDocId} from 'components/InfinityList';
import {
	createZIPXL,
	IArrayRoles,
	renderArrayAsRichtText,
	renderAttachmentsXL,
	renderDateXL,
	renderHeaderXL,
	renderHTMLStringXL,
	renderPhaseDateXL,
	renderRichtText,
} from 'components/InfinityList/Excel/ExcelExportCommon';
import {renderParagraphListXL} from './XLParagraphList';

interface IRegulationVersionDetailPage {
	(
		entityListItems: any[],
		arrRoles: IArrayRoles,
		pageDetails: any,
		t: TFunction,
	): Promise<void>;
}

export const handleClickRegulationVersionDetailPage: IRegulationVersionDetailPage =
	async (entityListItems, arrRoles, pageDetails, t) => {
		const arrTables: string[] = [];
		const arrImagesUri: string[] = [];
		const arrImagesName: string[] = [];

		const arrParaFilt = entityListItems as RegulatoryDocumentParagraph[];
		const doc = pageDetails.regulatoryDocumentDetails as RegulatoryDocument;
		const reapprovingHistoryMessages =
			pageDetails.reapprovingHistoryMessages as any[];
		const isFinalised = doc.workflow.status === WorkflowStatus.Finalized;

		const getHeadingRegDoc = (rawHeading: string) => {
			return t(rawHeading, {
				ns: 'features/regulatorydocuments',
				keyPrefix: 'RegulatoryDocumentDetailsPage',
			});
		};

		const getHeadingRegulation = (rawHeading: string) => {
			return t(rawHeading, {
				ns: 'features/regulations',
				keyPrefix: 'RegulationsList',
			});
		};

		const getHeadingPogis = (rawHeading: string) => {
			return t(rawHeading, {
				ns: 'features/pogisdocuments',
				keyPrefix: 'Reference',
			});
		};

		const getDetailsSectionT = (rawHeading: string) => {
			return t(rawHeading, {
				ns: 'components/detailssection',
			});
		};

		const workbook = new Excel.Workbook();
		const strPageName = getHeadingRegDoc('ParagraphsHeader');
		const sheet = workbook.addWorksheet('TRACE_' + strPageName);
		const standardWidth = 25;
		const numColsAfterDefinition = 14;

		const arrColWidth: number[] = [
			isFinalised ? 50 : 30,
			100,
			...new Array(2).fill(standardWidth),
			isFinalised ? 100 : 30,
			...new Array(numColsAfterDefinition).fill(standardWidth),
		];

		renderHeaderXL(workbook, sheet, arrColWidth, strPageName, t);

		let intRowCount = 4;
		let row = sheet.getRow(intRowCount);
		row.alignment = {wrapText: true, vertical: 'top'};

		row.getCell('B').value = renderRichtText(
			getHeadingRegulation('RegulationNumber'),
			doc.regulation?.regulationNumber ?? '',
		);

		row.getCell('C').value = renderRichtText(
			getHeadingRegulation('Name'),
			doc.regulation?.name ?? '',
		);

		row.getCell('D').value = renderRichtText(
			getHeadingRegDoc('RegulatoryDocuments'),
			doc.name,
		);

		intRowCount++;
		row = sheet.getRow(intRowCount);
		row.alignment = {wrapText: true, vertical: 'top'};

		row.getCell('B').value = renderRichtText(
			getHeadingRegDoc('StatusLabel'),
			t(doc.status ? doc.status : '', {
				ns: 'common/enums',
				keyPrefix: 'RegulatoryDocumentStatus',
			}),
		);
		row.getCell('C').value = renderHTMLStringXL(
			getHeadingRegDoc('ShortSummaryLabel'),
			doc.summary,
		);
		row.getCell('D').value = renderArrayAsRichtText(
			getHeadingRegDoc('KeywordsLabel'),
			doc.keywords,
		);

		intRowCount++;

		row = sheet.getRow(intRowCount);
		row.alignment = {wrapText: true, vertical: 'top'};

		const pogisIds = (doc.pogisDocuments ?? []).map(x => x.pogisId).join(';\n');
		const pogistext =
			doc.pogisExists === true
				? `${getHeadingPogis('yes')}\n${pogisIds}`
				: getHeadingPogis('no');
		row.getCell('B').value = renderRichtText(
			getHeadingPogis('AlreadyCommunicated'),
			pogistext,
		);

		const historyText =
			reapprovingHistoryMessages.length > 0
				? reapprovingHistoryMessages
						.map(x => `(${formatDateTime(x.date, i18n)}): ${x.value}`)
						.join('\n')
				: getDetailsSectionT('ChangeHistoryEmptyMessage');
		row.getCell('C').value = renderRichtText(
			getHeadingRegDoc('ChangeHistoryLabel'),
			historyText,
		);

		row.getCell('D').value = renderRichtText(
			getHeadingRegDoc('DisassemblyStatus'),
			t(
				doc.disassemblyStatus ??
					RegulatoryDocumentDisassemblyStatus.DisassemblyNotRequired,
				{
					ns: 'common/enums',
					keyPrefix: 'RegulatoryDocumentDisassemblyStatus',
				},
			),
		);

		row.getCell('E').value = renderRichtText(
			getHeadingRegDoc('WorkflowStatus'),
			t(doc?.workflow?.status ?? WorkflowStatus.New, {
				ns: 'common/enums',
				keyPrefix: 'WorkflowStatus',
			}),
		);

		intRowCount++;
		row = sheet.getRow(intRowCount);
		row.alignment = {wrapText: true, vertical: 'top'};

		row.getCell('B').value = renderRichtText(
			getHeadingRegDoc('EffectiveDateLabel'),
			renderDateXL(doc.dateEffective),
		);

		row.getCell('C').value = renderRichtText(
			getHeadingRegDoc('ExpirationDateLabel'),
			renderDateXL(doc.dateExpiration),
		);

		intRowCount++;
		row = sheet.getRow(intRowCount);
		row.alignment = {wrapText: true, vertical: 'top'};

		row.getCell('B').value = renderRichtText(
			getHeadingRegDoc('NewRegistrationLabel'),
			renderDateXL(doc.dateNewRegistration),
		);

		row.getCell('C').value = renderRichtText(
			getHeadingRegDoc('NewTypesLabel'),
			renderDateXL(doc.dateNewTypes),
		);

		row.getCell('D').value = renderRichtText(
			getHeadingRegDoc('ModelYearLabel'),
			doc.modelYear ? (doc.modelYear as unknown as string) : '',
		);

		row.getCell('E').value = renderRichtText(
			getHeadingRegDoc('PhaseInLabel'),
			renderPhaseDateXL(doc.phaseIn),
		);

		row.getCell('F').value = renderRichtText(
			getHeadingRegDoc('PhaseOutLabel'),
			renderPhaseDateXL(doc.phaseOut),
		);

		intRowCount = renderAttachmentsXL(
			intRowCount,
			getHeadingRegDoc('AttachmentsLabel'),
			doc.attachments,
			sheet,
			t,
		);

		intRowCount++;
		intRowCount++;

		renderParagraphListXL(
			intRowCount,
			true,
			arrTables,
			arrImagesName,
			arrImagesUri,
			arrParaFilt,
			arrRoles,
			sheet,
			t,
		);

		const strZipName = strPageName + '_' + funcDecodeHex24ToDocId(doc.id);
		await createZIPXL(
			workbook,
			strZipName,
			t,
			arrTables,
			arrImagesUri,
			arrImagesName,
			doc.attachments!,
		);
	};
